import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type Value } from 'react-phone-number-input';

import { useLockBodyScroll } from '@/hooks';
import { Icons } from '@/utils/Icons';
import { showToast } from '@/utils';
import { phoneSchema, resetPasswordSchema } from '@/utils/validation';
import { useForgotPasswordMutation, useResetPasswordMutation } from '@/store/api/authApi';
import PhoneInput from '@/components/common/PhoneInput';

type Props = {
  open: boolean;
  onClose: () => void;
};

type PhoneFormValues = z.infer<typeof phoneSchema>;
type ResetFormValues = z.infer<typeof resetPasswordSchema>;

const ForgotPasswordModal: React.FC<Props> = ({ open, onClose }) => {
  const [forgotPassword, { isLoading: sendingOtp }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: resetting }] = useResetPasswordMutation();

  const [step, setStep] = useState<'phone' | 'reset'>('phone');
  const [mobile, setMobile] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useLockBodyScroll(open);

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: { mobile: '' },
  });

  const resetForm = useForm<ResetFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { otp: '', newPassword: '', confirmPassword: '' },
  });

  const handleClose = () => {
    setStep('phone');
    setMobile('');
    phoneForm.reset();
    resetForm.reset();
    onClose();
  };

  if (!open) return null;

  const onSendOtp = async (data: PhoneFormValues) => {
    try {
      await forgotPassword({ mobile: data.mobile }).unwrap();
      setMobile(data.mobile);
      setStep('reset');
      showToast('OTP sent to your mobile number', 'success');
    } catch (error: any) {
      showToast(error?.data?.message || 'Failed to send OTP', 'error');
    }
  };

  const onReset = async (data: ResetFormValues) => {
    try {
      await resetPassword({
        mobile,
        newPassword: data.newPassword,
        code: data.otp,
      }).unwrap();
      showToast('Password reset successfully, please login', 'success');
      handleClose();
    } catch (error: any) {
      showToast(error?.data?.message || 'Failed to reset password', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary/40">
      <div className="w-[420px] max-w-[92%] bg-surface rounded-md shadow-auth-card mx-5">
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-[20px] font-medium">Forgot Password</h2>
          <button type="button" onClick={handleClose} aria-label="Close">
            <Icons.Close size={18} />
          </button>
        </div>

        {step === 'phone' && (
          <form onSubmit={phoneForm.handleSubmit(onSendOtp)} className="px-5 pb-5 space-y-4">
            <p className="text-body-muted text-sm">
              Enter your registered mobile number, we&apos;ll send you a verification code.
            </p>
            <div className="relative">
              <Controller
                name="mobile"
                control={phoneForm.control}
                render={({ field }) => (
                  <PhoneInput
                    value={field.value as Value}
                    onChange={(phoneData) => field.onChange(phoneData.fullNumber)}
                  />
                )}
              />
              {phoneForm.formState.errors.mobile && (
                <p className="text-danger text-xs mt-1 ml-[5px]">
                  {phoneForm.formState.errors.mobile.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={sendingOtp}
              className="w-full bg-primary text-primary-foreground py-2 rounded-sm disabled:opacity-60"
            >
              {sendingOtp ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={resetForm.handleSubmit(onReset)} className="px-5 pb-5 space-y-4">
            <p className="text-body-muted text-sm">
              Enter the code sent to {mobile} and choose a new password.
            </p>

            <div className="relative">
              <input
                {...resetForm.register('otp')}
                placeholder="Verification code"
                className="w-full border border-input-border h-9 px-2 text-[16px] focus:outline-none rounded-sm"
              />
              {resetForm.formState.errors.otp && (
                <p className="text-danger text-xs mt-1 ml-[5px]">
                  {resetForm.formState.errors.otp.message}
                </p>
              )}
            </div>

            <div className="relative">
              <div className="flex items-center border border-input-border rounded-sm h-9 overflow-hidden">
                <input
                  type={showNew ? 'text' : 'password'}
                  placeholder="New password"
                  className="flex-1 h-full px-2 text-[16px] outline-none"
                  {...resetForm.register('newPassword')}
                  onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="w-10 h-full flex items-center justify-center"
                >
                  {showNew ? <Icons.EyeSlash size={16} /> : <Icons.Eye size={16} />}
                </button>
              </div>
              {resetForm.formState.errors.newPassword && (
                <p className="text-danger text-xs mt-1 ml-[5px]">
                  {resetForm.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="relative">
              <div className="flex items-center border border-input-border rounded-sm h-9 overflow-hidden">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  className="flex-1 h-full px-2 text-[16px] outline-none"
                  {...resetForm.register('confirmPassword')}
                  onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="w-10 h-full flex items-center justify-center"
                >
                  {showConfirm ? <Icons.EyeSlash size={16} /> : <Icons.Eye size={16} />}
                </button>
              </div>
              {resetForm.formState.errors.confirmPassword && (
                <p className="text-danger text-xs mt-1 ml-[5px]">
                  {resetForm.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={resetting}
              className="w-full bg-primary text-primary-foreground py-2 rounded-sm disabled:opacity-60"
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
